import { useRouteContext } from '@/app/layouts/TenantLayout'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { ErrorMessage } from '@/components/ui/errormessage'
import { Input } from '@/components/ui/input'
import { SidebarMenu, SidebarMenuItem } from '@/components/ui/sidebar'
import { useCurrentPlan } from '@/hooks/access-control'
import { isOrganizationLevelRoute } from '@/lib/routerUtils'
import { cn, getErrorMessage } from '@/lib/utils'
import type { PlatformUser } from '@/stores/auth'
import { useAuthStore, useUser } from '@/stores/authStore'
import { isConsoleDeployment } from '@/utils/deployment'
import { useIsAdmin, useIsOwner } from '@/utils/permissions'
import { getPlanDisplayName } from '@/utils/subscription'
import { Link, useLocation } from '@tanstack/react-router'
import { Activity, Check, ChevronsUpDown, CreditCard, LogOut, Pencil, Settings, X } from 'lucide-react'
import { useCallback, useState } from 'react'
import { toast } from 'sonner'

export function NavUser() {
	const { tenantId, orgId } = useRouteContext()
	const location = useLocation()
	const user = useUser() as PlatformUser | null
	const isOrgLevelRoute = isOrganizationLevelRoute(location.pathname)
	const isAdmin = useIsAdmin()
	const isOwner = useIsOwner()

	const userName = user?.name || 'User'
	const userEmail = user?.email || ''
	const initials = userName
		.split(' ')
		.map(n => n[0])
		.join('')
		.toUpperCase()
		.slice(0, 2)

	const isOrgContext = isOrgLevelRoute || !tenantId

	const planId = useCurrentPlan()
	const planName = planId ? `${getPlanDisplayName(planId)} Plan` : 'Free Plan'

	const settingsPath = (() => {
		if (!orgId || isConsoleDeployment()) {
			return null
		}
		if (isOrgContext) {
			return `/orgs/${orgId}/settings/general`
		}
		if (!tenantId) {
			return null
		}
		const projectSettingsBase = `/orgs/${orgId}/projects/${tenantId}/settings`
		return isAdmin ? `${projectSettingsBase}/general` : `${projectSettingsBase}/metadata`
	})()

	const settingsLabel = isOrgContext ? 'Organization Settings' : 'Project Settings'
	const showSettings = Boolean(settingsPath)

	// TODO: needs discussion — move monitoring URL to an environment variable instead of deriving from host
	const monitoringUrl = `https://monitor.${window.location.host}/`

	const handleMonitoringClick = useCallback(() => {
		window.open(monitoringUrl, '_blank', 'noopener,noreferrer')
	}, [monitoringUrl])

	const [isEditingName, setIsEditingName] = useState(false)
	const [editedName, setEditedName] = useState('')
	const [isSavingName, setIsSavingName] = useState(false)
	const [nameError, setNameError] = useState('')

	const openEditName = useCallback(() => {
		setEditedName(userName)
		setNameError('')
		setIsEditingName(true)
	}, [userName])

	const cancelEditName = useCallback(() => {
		setIsEditingName(false)
		setNameError('')
	}, [])

	const handleSaveName = useCallback(async () => {
		const trimmed = editedName.trim()
		if (!trimmed) {
			setNameError('Name is required')
			return
		}
		setIsSavingName(true)
		try {
			await useAuthStore.getState().updateUserName(trimmed)
			toast.success('Name updated successfully')
			setIsEditingName(false)
		} catch (error) {
			toast.error(getErrorMessage(error))
		} finally {
			setIsSavingName(false)
		}
	}, [editedName])

	return (
		<SidebarMenu>
			<SidebarMenuItem>
				<div className='flex items-center gap-2 px-1.5 py-1.5 bg-transparent border-none rounded-lg transition-colors hover:bg-sidebar-accent/30 has-[[data-state=open]]:bg-sidebar-accent/30'>
					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<div className='w-full flex items-center justify-between min-w-0 text-left cursor-pointer outline-none p-1 rounded-lg group select-none'>
								<div className='flex items-center gap-2 min-w-0 flex-1'>
									<Avatar className='size-9 rounded-lg border border-border shrink-0 bg-muted'>
										<AvatarFallback className='rounded-lg text-xs font-semibold bg-muted text-muted-foreground'>
											{initials}
										</AvatarFallback>
									</Avatar>
									<div className='flex flex-col min-w-0 justify-center leading-tight'>
										<span className='truncate text-body font-semibold text-muted-foreground group-hover:text-foreground group-data-[state=open]:text-foreground transition-colors'>
											{userName}
										</span>
										<span className='truncate text-badge text-muted-foreground/60 mt-px font-normal'>{planName}</span>
									</div>
								</div>
								<div className='flex items-center gap-2 shrink-0 ml-1.5'>
									<ChevronsUpDown className='size-(--icon-standalone) text-muted-foreground hover:text-foreground transition-colors cursor-pointer' />
								</div>
							</div>
						</DropdownMenuTrigger>

						<DropdownMenuContent
							className='z-100 w-(--sidebar-width) rounded-lg shadow-lg border border-border bg-popover text-popover-foreground'
							side='top'
							align='start'
							sideOffset={8}>
							{/* User info header */}
							<div className='flex items-center gap-2 px-3 py-2.5 min-w-0'>
								<Avatar className='size-10 rounded-lg border border-border shrink-0 bg-muted'>
									<AvatarFallback className='rounded-lg text-sm font-semibold bg-muted text-muted-foreground'>
										{initials}
									</AvatarFallback>
								</Avatar>
								<div className='flex flex-col min-w-0 flex-1 leading-none'>
									{isEditingName ? (
										<div className='flex items-center gap-1 min-w-0'>
											<Input
												autoFocus
												value={editedName}
												onChange={e => {
													setEditedName(e.target.value)
													if (nameError) {
														setNameError('')
													}
												}}
												onKeyDown={e => {
													if (e.key === 'Enter') {
														e.preventDefault()
														void handleSaveName()
													} else if (e.key === 'Escape') {
														e.preventDefault()
														e.stopPropagation()
														cancelEditName()
													}
												}}
												disabled={isSavingName}
												className={cn('h-6 text-body bg-card px-1.5', nameError && 'border-destructive')}
											/>
											<button
												type='button'
												aria-label='Save name'
												disabled={isSavingName}
												className='shrink-0 text-muted-foreground hover:text-foreground transition-colors cursor-pointer disabled:opacity-50'
												onClick={() => void handleSaveName()}>
												<Check className='size-3.5' />
											</button>
											<button
												type='button'
												aria-label='Cancel edit'
												disabled={isSavingName}
												className='shrink-0 text-muted-foreground hover:text-foreground transition-colors cursor-pointer disabled:opacity-50'
												onClick={cancelEditName}>
												<X className='size-3.5' />
											</button>
										</div>
									) : (
										<div className='flex items-center justify-between gap-2 min-w-0'>
											<span className='flex items-center gap-1 min-w-0'>
												<span className='truncate text-body font-semibold text-foreground'>{userName}</span>
												{!isConsoleDeployment() && (
													<button
														type='button'
														aria-label='Edit name'
														className='shrink-0 text-muted-foreground hover:text-foreground transition-colors cursor-pointer'
														onClick={openEditName}>
														<Pencil className='size-3' />
													</button>
												)}
											</span>
											{user?.role?.name && (
												<span className='text-badge font-medium px-1.5 py-0.5 rounded-full bg-primary/10 text-primary capitalize shrink-0'>
													{user.role.name}
												</span>
											)}
										</div>
									)}
									{nameError && <ErrorMessage message={nameError} />}
									<span className='truncate text-control text-muted-foreground mt-1 font-normal'>{userEmail}</span>
								</div>
							</div>
							<DropdownMenuSeparator />

							{isOrgContext && isOwner && orgId && (
								<DropdownMenuItem
									className='px-3 py-2 text-body flex items-center gap-(--spacing-icon-text) cursor-pointer'
									onClick={() =>
										window.open(
											`https://console.bytebeam.io/orgs/${orgId}/billing/plan-details`,
											'_blank',
											'noopener,noreferrer',
										)
									}>
									<CreditCard className='size-(--icon-standalone) text-muted-foreground' />
									<span>Billing</span>
								</DropdownMenuItem>
							)}

							{/* Monitoring — org-level only */}
							{isOrgContext && (
								<DropdownMenuItem
									className='px-3 py-2 text-body flex items-center gap-(--spacing-icon-text) cursor-pointer'
									onClick={handleMonitoringClick}>
									<Activity className='size-(--icon-standalone) text-muted-foreground' />
									<span>Monitoring</span>
								</DropdownMenuItem>
							)}

							{/* Settings */}
							{showSettings && settingsPath && (
								<DropdownMenuItem className='px-3 py-2 text-body cursor-pointer' asChild>
									<Link
										to={settingsPath}
										data-testid='nav-user-settings'
										className='flex items-center gap-(--spacing-icon-text) w-full'>
										<Settings className='size-(--icon-standalone) text-muted-foreground' />
										<span>{settingsLabel}</span>
									</Link>
								</DropdownMenuItem>
							)}
							{/* What's New */}
							{/* <DropdownMenuItem className='gap-2 cursor-pointer' asChild>
								<Link to='/whats-new'>
									<Sparkles className='size-(--icon-standalone)' />
									<span className='text-control'>What's New</span>
								</Link>
							</DropdownMenuItem> */}

							{/* Keyboard Shortcuts */}
							{/* <DropdownMenuItem className='gap-2 cursor-pointer'>
								<Keyboard className='size-(--icon-standalone)' />
								<span className='text-control'>Keyboard Shortcuts</span>
								<span className='ml-auto flex items-center gap-0.5 text-muted-foreground'>
									<Command className='size-3' />
									<span className='text-xs'>/</span>
								</span>
							</DropdownMenuItem> */}

							<DropdownMenuSeparator />

							{/* Signout */}
							<DropdownMenuItem
								variant='destructive'
								className='px-3 py-2 text-body flex items-center gap-(--spacing-icon-text) cursor-pointer text-destructive'
								onClick={() => useAuthStore.getState().logout()}>
								<LogOut className='size-(--icon-standalone)' />
								<span>Signout</span>
							</DropdownMenuItem>
						</DropdownMenuContent>
					</DropdownMenu>
				</div>
			</SidebarMenuItem>
		</SidebarMenu>
	)
}
